<template>
  <!-- Teleported to <body>: below 768px main.css gives every element an identity transform, which
       would pin this fixed stack to its ancestor instead of the viewport. Bottom-left of the
       content area on desktop (clear of the 236px sidebar), above the mobile tab bar below
       1024px. z-[210] keeps them above modals (ResponsiveModal 101, attachment preview 200) so an
       error raised from inside a dialog is still seen. The container is always rendered, so it is a
       live region that exists before any toast is added (content inserted together with its own
       role is announced unreliably); only errors take role="alert". The vue-toastification stack
       (see main.ts / main.css) shares this corner and is lifted above this one through the
       --wb-toast-offset variable set below. -->
  <Teleport to="body">
    <div
      ref="stackEl"
      aria-live="polite"
      class="pointer-events-none fixed left-4 right-4 bottom-[calc(72px+var(--safe-area-inset-bottom)+12px)] z-[210] flex flex-col items-start gap-2.5 lg:left-[260px] lg:right-auto lg:bottom-6"
    >
      <TransitionGroup
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-for="notification in notifications"
          :key="notification.id"
          :role="notification.type === 'error' ? 'alert' : undefined"
          :class="[
            'pointer-events-auto flex w-full items-center gap-3 rounded-[10px] bg-ink px-4 py-3 text-[13.5px] text-white shadow-[0_8px_24px_rgba(28,27,25,0.24)] lg:w-auto lg:min-w-[320px] lg:max-w-[420px]',
            notification.type === 'reminder' ? 'cursor-pointer transition-colors hover:bg-ink-2' : '',
          ]"
          @click="notification.type === 'reminder' ? handleReminderClick(notification) : undefined"
        >
          <span class="wb-toast-dot" :class="`wb-toast-dot--${notification.type}`" aria-hidden="true" />
          <div class="min-w-0 flex-1">
            <p class="font-medium">
              {{ notification.title }}
            </p>
            <p v-if="notification.message" class="mt-0.5 text-white/80">
              {{ notification.message }}
            </p>
            <!-- Rich reminder details -->
            <div v-if="notification.type === 'reminder' && notification.entityName" class="mt-1.5 flex items-center gap-1.5">
              <span class="inline-flex items-center rounded-md bg-white/10 px-1.5 py-0.5 text-xs font-medium capitalize text-white">
                {{ notification.activityType }}
              </span>
              <span class="text-xs text-white/80">
                {{ notification.entityName }}
              </span>
            </div>
            <p v-if="notification.type === 'reminder' && notification.entityId" class="mt-1 text-xs text-white/80">
              Click to view activity
            </p>
          </div>
          <button
            type="button"
            class="-my-3 -mr-3 grid h-11 w-11 flex-none place-items-center rounded-lg text-white/80 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white lg:-my-1.5 lg:-mr-2.5 lg:h-8 lg:w-8"
            @click.stop="removeNotification(notification.id)"
          >
            <span class="sr-only">Close</span>
            <XMarkIcon class="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useNotificationStore, type Notification } from '@/stores/notifications'
import { notificationTarget } from '@/lib/notificationRoute'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const router = useRouter()
const notificationStore = useNotificationStore()
const { notifications } = storeToRefs(notificationStore)
const { removeNotification } = notificationStore

// The vue-toastification stack (demo guard, org fallback, settings saves) lives in the same corner.
// Publish this stack's height so that one sits above it instead of overlapping.
const stackEl = ref<HTMLElement | null>(null)
let stackObserver: ResizeObserver | undefined

onMounted(() => {
  const el = stackEl.value
  if (!el || typeof ResizeObserver === 'undefined') return
  const root = document.documentElement
  stackObserver = new ResizeObserver(() => {
    const h = Math.ceil(el.getBoundingClientRect().height)
    root.style.setProperty('--wb-toast-offset', h > 0 ? `${h + 10}px` : '0px')
  })
  stackObserver.observe(el)
})

onBeforeUnmount(() => {
  stackObserver?.disconnect()
  document.documentElement.style.removeProperty('--wb-toast-offset')
})

// The status dot colours live in main.css (`.wb-toast-dot--<type>`), shared with the
// vue-toastification stack: the solid semantic colours are too dim on the ink toast.

function handleReminderClick(notification: Notification) {
  // The thread or record it is about (lib/notificationRoute.ts). A reminder also carries its
  // activity id onto a record page; thread targets already have their own query.
  const target = notificationTarget(notification)
  if (target) {
    router.push(
      notification.activityId && !target.includes('?')
        ? { path: target, query: { activityId: notification.activityId } }
        : target,
    )
  }
  removeNotification(notification.id)
}
</script>
